import React, { useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import AuditStateMessage from 'app/shared/modules/EntityAuditHistory/components/AuditStateMessage';

import { TemplateChangesStatus } from '../../FormChangeHistory.constants';
import { TemplateChangesPropType } from '../../FormChangeHistory.propTypes';
import { changesetStyles } from '../../FormChangeHistory.styles';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import {
  getPartTotalsLines,
  getPrismChangesMessage,
  getTemplatePartGroups,
} from '../../FormChangeHistory.utils';
import PartGroupAccordion from '../PartGroupAccordion';

/**
 * A Prism template's changed parts by group (dynamic fields, signatures, text, images, layout), or
 * why it lists none. The groups start closed, so the panel reads as a summary; one opens at a time.
 */
const PrismTemplateParts = ({ templateChanges }) => {
  const [openGroup, setOpenGroup] = useState(null);

  const groups = useMemo(
    () => getTemplatePartGroups(templateChanges.parts),
    [templateChanges.parts],
  );

  const message = getPrismChangesMessage(templateChanges);

  if (message) {
    return (
      <Box sx={changesetStyles}>
        <AuditStateMessage
          dataTestId={FormChangeHistoryTestIds.TEMPLATE_STATE}
          isFramed={false}
          message={message}
        />
        {templateChanges.status === TemplateChangesStatus.REIMPORTED &&
          getPartTotalsLines(templateChanges.totals).map(line => (
            <Typography
              color="text.secondary"
              data-testid={FormChangeHistoryTestIds.REIMPORTED_TOTALS}
              key={line.side}
              variant="regular12"
            >
              {line.text}
            </Typography>
          ))}
      </Box>
    );
  }

  return (
    <Box data-testid={FormChangeHistoryTestIds.PRISM_CHANGESET}>
      {groups.map(group => (
        <PartGroupAccordion
          group={group}
          isExpanded={openGroup === group.group}
          key={group.group}
          onToggle={setOpenGroup}
        />
      ))}
    </Box>
  );
};

PrismTemplateParts.propTypes = {
  templateChanges: TemplateChangesPropType.isRequired,
};

export default PrismTemplateParts;
